import React, {useCallback, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {getButton, Modal} from '@optimaxdev/design-system';
import {Icon} from '@optimaxdev/icons';
import classNames from 'classnames';

import {CART_SECTION} from 'constants/analytics/events/cart';
import {PDP_BONUS_OFFER} from 'constants/analytics/events/pdp';

import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';

import {getAdvantagesConfig} from './advantages.const';
import {PropsType, PopupType, AdvatageIconType} from './advantagesType';
import {FreeShippingContent} from './freeShippingContent/freeShippingContent';
import {POPUP} from './internal';
import {MoneyBackGuaranteeContent} from './moneyBackGuarantee/moneyBackGuaranteeContent';

import s from './advantages.scss';

/**
 * Advantages in base product info
 * @params {PropsType} props - component props
 * @return {React.ReactElement} component
 */
export const Advantages: React.FC<PropsType> = ({analyticTracker}) => {
    const [isMoneyPopupVisible, setMoneyPopupVisible] = useState<boolean>(false);
    const [isShippingPopupVisible, setShippingPopupVisible] = useState<boolean>(false);

    const toggleVisibility = useCallback(
        (popup: PopupType) => () => {
            if (popup === POPUP.MONEY) setMoneyPopupVisible(!isMoneyPopupVisible);
            else if (popup === POPUP.SHIPPING) setShippingPopupVisible(!isShippingPopupVisible);
        },
        [isMoneyPopupVisible, isShippingPopupVisible],
    );
    const handleClick = useCallback(
        (popup: PopupType) => () => {
            spawnPriorityTask(() => {
                if (analyticTracker === 'baseInfo') {
                    sendDesktopAnalyticsEvent(
                        PDP_BONUS_OFFER,
                        popup === POPUP.MONEY ? 'Money Back Guarantee' : 'Free Shipping & Returns',
                    );
                } else if (analyticTracker === 'cart') {
                    sendDesktopAnalyticsEvent(CART_SECTION, {
                        label:
                            popup === POPUP.MONEY
                                ? 'Money Back Guarantee'
                                : 'Free Shipping & Returns',
                        section: 'Right',
                    });
                }
            });
            toggleVisibility(popup)();
        },
        [analyticTracker, toggleVisibility],
    );

    return (
        <>
            <div className={s.advantages}>
                {getAdvantagesConfig({
                    onClick: handleClick,
                }).map(({content, enabled, icon, iconSize, id, onClick}) => {
                    const iconProps: AdvatageIconType = {icon};
                    if (iconSize) iconProps.size = iconSize;
                    return enabled ? (
                        <div
                            {...getButton({onClick, onKeyDown: onClick})}
                            className={classNames({[s.check]: id !== 'dscoShipping'}, s.item, {
                                [s.clickable]: onClick,
                            })}
                            data-testid={id}
                            key={id}
                        >
                            <div className={s.iconWrapper}>
                                <Icon {...iconProps} className={s.icon} />
                            </div>
                            <div className={s.content}>{content}</div>
                        </div>
                    ) : null;
                })}
            </div>

            <Modal
                isOpen={isMoneyPopupVisible}
                onRequestClose={toggleVisibility(POPUP.MONEY)}
                overlay="dark"
            >
                <MoneyBackGuaranteeContent />
            </Modal>

            <Modal
                isOpen={isShippingPopupVisible}
                onRequestClose={toggleVisibility(POPUP.SHIPPING)}
                overlay="dark"
            >
                <FreeShippingContent />
            </Modal>
        </>
    );
};
